import React from "react";
import logo from "./SMSIPS_logo.png";
import "./Facilities.css";

const learningResources = [
  {
    number: "01",
    title: "Library",
    description:
      "A well-stocked library supports reading, research, and independent learning.",
    image:
      "https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1200&q=85",
    alt: "Tall bookshelves in a library",
  },
  {
    number: "02",
    title: "Computer lab",
    description:
      "A dedicated computer lab gives students access to digital tools for their academic work.",
    image:
      "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=85",
    alt: "People working together with computers",
  },
  {
    number: "03",
    title: "Science lab",
    description:
      "A science lab supports students as they explore scientific ideas and practical learning.",
    image:
      "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=1200&q=85",
    alt: "Scientific laboratory equipment",
  },
];

const creativeSpaces = [
  {
    number: "04",
    title: "Music room",
    description:
      "A dedicated space for students to practise, learn, and explore their musical interests.",
    image:
      "https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=1200&q=85",
    alt: "Musician practising in a recording studio",
  },
  {
    number: "05",
    title: "Dance room",
    description:
      "A separate room for movement, dance practice, and developing performance skills.",
    image:
      "https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?auto=format&fit=crop&w=1200&q=85",
    alt: "Dancers rehearsing in a studio",
  },
  {
    number: "06",
    title: "Drama room",
    description:
      "A dedicated space for rehearsal, expression, and collaborative performance.",
    image:
      "https://images.unsplash.com/photo-1503095396549-807759245b35?auto=format&fit=crop&w=1200&q=85",
    alt: "Stage prepared for a theatre performance",
  },
  {
    number: "07",
    title: "Art room",
    description:
      "A separate creative space where students can experiment with materials and visual ideas.",
    image:
      "https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=1200&q=85",
    alt: "Artist working with colorful paints and materials",
  },
];

const beyondClassroom = [
  {
    number: "08",
    title: "Extracurricular activities",
    description:
      "Opportunities beyond academic study help students explore interests and develop their talents.",
    image:
      "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=85",
    alt: "Group taking part in an activity together",
  },
  {
    number: "09",
    title: "Clubs",
    description:
      "School clubs give students another way to discover interests and take part in the school community.",
    image:
      "https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=1200&q=85",
    alt: "Group collaborating around a table",
  },
  {
    number: "10",
    title: "Sports",
    description:
      "Sports teams offer students an opportunity to develop their talents through participation and teamwork.",
    image:
      "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1200&q=85",
    alt: "Team playing a ball sport on a field",
  },
  {
    number: "11",
    title: "Trips and expeditions",
    description:
      "Field trips and expeditions take learning beyond the classroom, including experiences outside the city.",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=85",
    alt: "Mountain landscape explored on an outdoor expedition",
  },
];

function Facilities() {
  return (
    <div className="App facilities-page">
      <header>
        <a className="logo" href="/" aria-label="SMSIPS home">
          <img src={logo} alt="School Logo" />
          <div className="school-info">
            <h1>Shri Maa Sharda International Public School</h1>
            <p className="affiliation">
              Affiliated to CBSE New Delhi, Affiliation Number: 2133305
            </p>
          </div>
        </a>
        <nav aria-label="Main navigation">
          <ul>
            <li><a href="/#about">About</a></li>
            <li><a href="/facilities" aria-current="page">Facilities</a></li>
            <li><a href="/#cbse">CBSE Mandatory Disclosure</a></li>
            <li><a href="/#contact">Contact Us</a></li>
            <li><a href="/#admissions">Apply Now</a></li>
          </ul>
        </nav>
      </header>

      <main className="facilities-main">
        <section className="facilities-intro" aria-labelledby="facilities-title">
          <p className="eyebrow">Learning spaces</p>
          <h2 id="facilities-title">Facilities for learning and discovery</h2>
          <p>
            Explore learning resources and opportunities that support students
            in their academic pursuits and interests.
          </p>
          <p className="facilities-photo-disclaimer">
            Photos are representative and may not show SMSIPS facilities.
          </p>
        </section>

        <section className="learning-resources" aria-label="Learning resources">
          <div className="resource-group-heading">
            <p className="eyebrow">Study and discovery</p>
            <h2>Learning resources</h2>
          </div>
          {learningResources.map((resource) => (
            <article className="resource-card" key={resource.title}>
              <img src={resource.image} alt={resource.alt} loading="lazy" />
              <div className="resource-card-content">
                <span className="resource-number">{resource.number}</span>
                <h3>{resource.title}</h3>
                <p>{resource.description}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="creative-section" aria-labelledby="creative-title">
          <div className="resource-group-heading">
            <p className="eyebrow">Create and perform</p>
            <h2 id="creative-title">Dedicated rooms for the arts</h2>
            <p>
              Separate rooms for music, dance, drama, and art give students
              spaces to practise, create, and express themselves.
            </p>
          </div>
          <div className="learning-resources creative-resources" aria-label="Creative arts rooms">
            {creativeSpaces.map((resource) => (
              <article className="resource-card" key={resource.title}>
                <img src={resource.image} alt={resource.alt} loading="lazy" />
                <div className="resource-card-content">
                  <span className="resource-number">{resource.number}</span>
                  <h3>{resource.title}</h3>
                  <p>{resource.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="beyond-section" aria-labelledby="beyond-title">
          <div className="resource-group-heading">
            <p className="eyebrow">Grow and participate</p>
            <h2 id="beyond-title">Beyond the classroom</h2>
            <p>
              Clubs, extracurricular activities, sports teams, and trips beyond
              the city give students ways to explore interests and develop their
              talents.
            </p>
          </div>
          <div className="learning-resources beyond-resources" aria-label="Beyond the classroom">
            {beyondClassroom.map((resource) => (
              <article className="resource-card" key={resource.title}>
                <img src={resource.image} alt={resource.alt} loading="lazy" />
                <div className="resource-card-content">
                  <span className="resource-number">{resource.number}</span>
                  <h3>{resource.title}</h3>
                  <p>{resource.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="facilities-contact">
          <div>
            <p className="eyebrow">Visit or get in touch</p>
            <h2>Learn more about SMSIPS</h2>
          </div>
          <a className="button button-primary" href="/#contact">
            Contact the school
          </a>
        </section>
      </main>

      <footer>
        <div>
          <strong>SMSIPS</strong>
          <span>Shri Maa Sharda International Public School</span>
        </div>
        <p>© 2023 Shri Maa Sharda International Public School. All Rights Reserved.</p>
        <a href="/" className="back-to-top">Home</a>
      </footer>
    </div>
  );
}

export default Facilities;